import api from './api';
import practiceService from './practiceService';

export const getMeasurementErrorMessage = (error, fallbackMessage = 'Something went wrong') => {
  const code = error?.response?.data?.code;
  if (code === 'PRACTICE_LOCKED_READ_ONLY') {
    return 'Practice is locked. You can view readings only. Contact administrator.';
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
};

const invalidatePatientListCache = (practiceId) => {
  try {
    practiceService.invalidatePracticePatientCaches(practiceId);
  } catch (_) {
    // ignore cache errors
  }
};

const measurementService = {
  // Create blood pressure measurement
  createBloodPressureMeasurement: async (practiceId, patientId, measurementData) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure`,
      measurementData
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  // Get blood pressure measurements
  getBloodPressureMeasurements: async (practiceId, patientId, params = {}) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure`,
      { params }
    );
    return response;
  },

  // Update blood pressure measurement
  updateBloodPressureMeasurement: async (practiceId, patientId, measurementId, measurementData) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure/${measurementId}`,
      measurementData
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  // Delete blood pressure measurement
  deleteBloodPressureMeasurement: async (practiceId, patientId, measurementId) => {
    const response = await api.delete(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure/${measurementId}`
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  // Get blood pressure measurements by date range (omit dates for full history / ALL)
  getBloodPressureByDateRange: async (practiceId, patientId, fromDate, toDate, sortBy = 'date') => {
    const params = { sortBy };
    if (fromDate && toDate) {
      params.fromDate = fromDate;
      params.toDate = toDate;
    }
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure`,
      { params }
    );
    return response;
  },

  // Sync measurement with ForaCare
  syncMeasurementWithForaCare: async (practiceId, patientId, measurementData) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/measurements/foracare-sync`,
      measurementData
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  // Blood Glucose Measurements
  createBloodGlucoseMeasurement: async (practiceId, patientId, measurementData) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose`,
      measurementData
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  getBloodGlucoseMeasurements: async (practiceId, patientId, params = {}) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose`,
      { params }
    );
    return response;
  },

  getBloodGlucoseByDateRange: async (practiceId, patientId, fromDate, toDate, sortBy = 'date') => {
    const params = { sortBy };
    if (fromDate && toDate) {
      params.fromDate = fromDate;
      params.toDate = toDate;
    }
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose`,
      { params }
    );
    return response;
  },

  updateBloodGlucoseMeasurement: async (practiceId, patientId, measurementId, measurementData) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose/${measurementId}`,
      measurementData
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  deleteBloodGlucoseMeasurement: async (practiceId, patientId, measurementId) => {
    const response = await api.delete(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose/${measurementId}`
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  // Weight Measurements
  createWeightMeasurement: async (practiceId, patientId, measurementData) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight`,
      measurementData
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  getWeightMeasurements: async (practiceId, patientId, params = {}) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight`,
      { params }
    );
    return response;
  },

  getWeightByDateRange: async (practiceId, patientId, fromDate, toDate, sortBy = 'date') => {
    const params = { sortBy };
    if (fromDate && toDate) {
      params.fromDate = fromDate;
      params.toDate = toDate;
    }
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight`,
      { params }
    );
    return response;
  },

  updateWeightMeasurement: async (practiceId, patientId, measurementId, measurementData) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight/${measurementId}`,
      measurementData
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  deleteWeightMeasurement: async (practiceId, patientId, measurementId) => {
    const response = await api.delete(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight/${measurementId}`
    );
    invalidatePatientListCache(practiceId);
    return response;
  },

  // Clinical Analysis
  getClinicalAnalysis: async (practiceId, patientId, readings) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/analysis/blood-pressure`,
      { readings }
    );
    return response;
  },

  getMeasurementAiSummary: async (practiceId, patientId, summary) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/analysis/measurement-summary`,
      { summary }
    );
    return response;
  }
};

export default measurementService;
